import { type ChangeEvent, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, Upload, X } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import {
  useProfileQuery,
  useUpdateProfile,
  useUploadAvatar,
  useRemoveAvatar,
  useToast,
} from '@/hooks'
import { profileFormSchema, type ProfileFormData } from '@/lib/validations'
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Textarea,
} from '@/components/ui'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'

const MAX_AVATAR_SIZE = 5 * 1024 * 1024 // 5MB

export default function Profile() {
  const { user } = useAuth()
  const { data, isLoading, isError, refetch } = useProfileQuery()
  const updateProfile = useUpdateProfile()
  const uploadAvatar = useUploadAvatar()
  const removeAvatarMutation = useRemoveAvatar()
  const { toast } = useToast()

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      firstName: user?.first_name ?? '',
      lastName: user?.last_name ?? '',
      bio: '',
      company: '',
      location: '',
    },
  })
  const {
    handleSubmit,
    reset,
    control,
    formState: { isDirty },
  } = form

  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [removeAvatar, setRemoveAvatar] = useState(false)
  const objectUrlRef = useRef<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (data) {
      reset({
        firstName: data.user.first_name ?? '',
        lastName: data.user.last_name ?? '',
        bio: data.bio ?? '',
        company: data.company ?? '',
        location: data.location ?? '',
      })

      if (!avatarFile && !removeAvatar) {
        setAvatarPreview(data.avatar_url ?? null)
      }
    }
  }, [data, reset, avatarFile, removeAvatar])

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
      }
    }
  }, [])

  const handleAvatarChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Please select an image file',
      })
      event.target.value = ''
      return
    }

    if (file.size > MAX_AVATAR_SIZE) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Profile photos must be smaller than 5MB',
      })
      event.target.value = ''
      return
    }

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
    }

    const previewUrl = URL.createObjectURL(file)
    objectUrlRef.current = previewUrl
    setAvatarPreview(previewUrl)
    setAvatarFile(file)
    setRemoveAvatar(false)
  }

  const handleRemoveAvatar = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
    }

    setAvatarPreview(null)
    setAvatarFile(null)
    setRemoveAvatar(true)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const onSubmit = async (values: ProfileFormData) => {
    try {
      // Update profile text fields
      await updateProfile.mutateAsync({
        first_name: values.firstName?.trim() ?? '',
        last_name: values.lastName?.trim() ?? '',
        bio: values.bio?.trim() ?? '',
        company: values.company?.trim() ?? '',
        location: values.location?.trim() ?? '',
      })

      // Handle avatar upload separately
      if (avatarFile) {
        await uploadAvatar.mutateAsync(avatarFile)
      } else if (removeAvatar) {
        await removeAvatarMutation.mutateAsync()
      }

      // Clean up
      if (avatarFile && objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
      setAvatarFile(null)
      setRemoveAvatar(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    } catch (error) {
      // Errors are handled in the mutation hooks via toast notifications.
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex-1 bg-background">
        <div className="mx-auto max-w-3xl p-6">
          <Card>
            <CardHeader className="space-y-3">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-4 w-48" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-32 w-32 rounded-full" />
              <div className="grid gap-4 md:grid-cols-2">
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
              </div>
              <Skeleton className="h-24" />
            </CardContent>
            <CardFooter>
              <Skeleton className="h-10 w-28" />
            </CardFooter>
          </Card>
        </div>
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen flex-1 bg-background">
        <div className="mx-auto max-w-3xl p-6">
          <Card className="border-destructive/30 bg-destructive/5">
            <CardHeader>
              <CardTitle className="text-destructive">Unable to load profile</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                We couldn't load your profile details. Please try again.
              </p>
            </CardContent>
            <CardFooter>
              <Button variant="outline" onClick={() => refetch()}>
                Retry
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex-1 bg-background">
      <div className="mx-auto max-w-3xl space-y-6 p-6">
        <div>
          <h1 className="text-3xl font-semibold text-foreground">Your Profile</h1>
          <p className="mt-2 text-muted-foreground">
            Update your personal details and profile photo.
          </p>
        </div>

        <Card>
          <Form {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-0">
              <CardContent className="space-y-8 pt-6">
                <div className="flex flex-col gap-6 md:flex-row md:items-center">
                  <Avatar className="h-32 w-32 border border-dashed border-border bg-muted">
                    {avatarPreview ? (
                      <AvatarImage src={avatarPreview} alt="Profile preview" />
                    ) : data.avatar_url ? (
                      <AvatarImage src={data.avatar_url} alt="Profile preview" />
                    ) : (
                      <AvatarFallback>
                        <Upload className="h-8 w-8 text-muted-foreground" />
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="avatar">Profile photo</Label>
                      <p className="mt-1 text-xs text-muted-foreground">PNG or JPG up to 5MB.</p>
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <input
                        ref={fileInputRef}
                        id="avatar"
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        Upload new photo
                      </Button>
                      {(avatarPreview || data.avatar_url) && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={handleRemoveAvatar}
                          className="text-destructive hover:text-destructive"
                        >
                          <X className="mr-2 h-4 w-4" />
                          Remove photo
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  <FormField
                    control={control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First name</FormLabel>
                        <FormControl>
                          <Input placeholder="Your first name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last name</FormLabel>
                        <FormControl>
                          <Input placeholder="Your last name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid gap-6 md:grid-cols-2">
                  <FormField
                    control={control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First name</FormLabel>
                        <FormControl>
                          <Input placeholder="Your first name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last name</FormLabel>
                        <FormControl>
                          <Input placeholder="Your last name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={control}
                  name="bio"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bio</FormLabel>
                      <FormControl>
                        <Textarea
                          rows={4}
                          placeholder="Tell clients a little about yourself"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid gap-6 md:grid-cols-2">
                  <FormField
                    control={control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company</FormLabel>
                        <FormControl>
                          <Input placeholder="Where do you work?" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name="location"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Location</FormLabel>
                        <FormControl>
                          <Input placeholder="Where are you based?" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
              <CardFooter className="flex items-center justify-end">
                <Button
                  type="submit"
                  disabled={
                    updateProfile.isPending ||
                    uploadAvatar.isPending ||
                    removeAvatarMutation.isPending ||
                    (!isDirty && !avatarFile && !removeAvatar)
                  }
                >
                  {updateProfile.isPending ||
                  uploadAvatar.isPending ||
                  removeAvatarMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save changes'
                  )}
                </Button>
              </CardFooter>
            </form>
          </Form>
        </Card>
      </div>
    </div>
  )
}
